import { inject, Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable, catchError, distinctUntilChanged, lastValueFrom, of, switchMap, take, shareReplay } from 'rxjs';
import { DocumentVerificationUpdateRTM } from '@gains-com/realtime';
import { environment } from '../../../environments/environment.js';
import { HttpClient } from '@angular/common/http';
import { LOGGER_FACTORY } from '../logger/logger-factory.provider.js';
import { UserViewService } from './user-view.service.js';
import { DocumentVerificationStatus, DocumentVerificationTypeId } from '@gains-com/shared';

export interface DocumentVerificationDTO {
  document_verification_id: string;
  user_id: string;
  last_check_evaluated_at: string | null;
  document_verification_submitted_at: string | null;
  document_verification_type_id: DocumentVerificationTypeId;
  target_entity_type_id: string;
  target_entity_id: string;
  status: DocumentVerificationStatus;
  total_points: number;
  evaluation_expires_at: string | null;
  approved: boolean;
  failed_permanently: boolean;
  finalized: boolean;
  finalized_at: string | null;
  created_at: string;
  updated_at: string;
}

export type DocumentVerificationVM = DocumentVerificationUpdateRTM | DocumentVerificationDTO;

/**
 * ViewState service for document verifications.
 *
 * This service manages all document verifications for the current user,
 * independent of payment methods. Verifications are loaded automatically
 * when a user with pii_submitted logs in.
 *
 * Usage:
 * - Subscribe to documentVerifications$ to reactively get all verifications
 * - Use updateVerification() with realtime events to keep state current
 * - Call syncDocumentVerifications() to force refresh from API
 *
 * @example
 * // In a component
 * ngOnInit() {
 *   this._viewService.documentVerifications$
 *     .pipe(takeUntilDestroyed(this._destroyRef))
 *     .subscribe(verifications => { ... });
 * }
 */
@Injectable({
  providedIn: 'root',
})
export class DocumentVerificationViewService {
  private readonly _http = inject(HttpClient);
  private readonly _logger = inject(LOGGER_FACTORY).create('DocumentVerificationViewService');
  private readonly _userView = inject(UserViewService);

  private readonly _state = signal<DocumentVerificationVM[] | null>(null);
  // Mirrors the lazy BehaviorSubject creation: writes are ignored until the
  // documentVerifications$ pipeline has loaded the user's verifications once.
  private _stateInitialized = false;
  private readonly _loading = signal<boolean>(true);

  /** Readonly signal surface for new consumers. */
  readonly documentVerifications = this._state.asReadonly();
  readonly loading = this._loading.asReadonly();

  /** Backward-compat stream shim re-sourced from the signal store. */
  private readonly _state$ = toObservable(this._state);

  private _fetchDocumentVerifications$ = () => {
    return this._http.get<DocumentVerificationDTO[]>(`${environment.apiUrl}/document-verification`).pipe(
      take(1),
      catchError((err) => {
        this._logger.error('Failed to fetch document verifications', { error: err });
        return of(null);
      }),
    );
  };

  /**
   * Observable that emits all document verifications for the current user.
   * Automatically loads when user with pii_submitted logs in.
   * Returns null if user is not logged in or hasn't submitted PII.
   */
  documentVerifications$: Observable<DocumentVerificationVM[] | null> = this._userView.state$.pipe(
    distinctUntilChanged((u1, u2) => u1?.user_id === u2?.user_id && u1?.pii_submitted === u2?.pii_submitted),
    switchMap((user) => {
      if (!user || !user.pii_submitted) {
        if (this._stateInitialized) {
          this._state.set(null);
        }
        return of(null);
      }

      return this._fetchDocumentVerifications$().pipe(
        switchMap((verifications) => {
          // Update the existing store, or mark it initialized on first load.
          // Avoids the race condition that completing/recreating a subject caused.
          this._state.set(verifications ?? []);
          this._stateInitialized = true;
          this._loading.set(false);
          return this._state$;
        }),
      );
    }),
    shareReplay(1),
  );

  /**
   * Observable that emits the loading state.
   */
  loading$: Observable<boolean> = toObservable(this._loading);

  /**
   * Get a specific verification by ID (synchronous from cache).
   *
   * @param verificationId The document verification ID
   * @returns The verification or null if not found
   */
  getVerificationById(verificationId: string): DocumentVerificationVM | null {
    const verifications = this._state() ?? [];
    return verifications.find((v) => v.document_verification_id === verificationId) ?? null;
  }

  /**
   * Get verifications for a specific target entity (synchronous from cache).
   * Useful for finding verifications associated with a specific payment method or other entity.
   *
   * @param entityTypeId The target entity type ID
   * @param entityId The target entity ID
   * @returns Array of verifications for the entity (may be empty)
   */
  getVerificationsForEntity(entityTypeId: string, entityId: string): DocumentVerificationVM[] {
    const verifications = this._state() ?? [];
    return verifications.filter((v) => v.target_entity_type_id === entityTypeId && v.target_entity_id === entityId);
  }

  /**
   * Replaces the entire document verifications array atomically.
   * Use this when you have the complete list (e.g., from API response).
   *
   * @param verifications Complete array of verifications to replace the current state
   */
  setDocumentVerifications(verifications: DocumentVerificationVM[]) {
    this._setStateIfInitialized(verifications);
  }

  /**
   * Writes to the store only once it has been initialized by the
   * documentVerifications$ pipeline — mirrors the old optional-chained
   * `this._state?.next(...)` which no-op'd before the subject existed.
   */
  private _setStateIfInitialized(verifications: DocumentVerificationVM[] | null) {
    if (this._stateInitialized) {
      this._state.set(verifications);
    }
  }

  /**
   * Updates a single document verification from realtime events.
   * Only updates if the incoming event is newer than current value.
   *
   * @param update The verification to update or add
   */
  updateVerification(update: DocumentVerificationUpdateRTM) {
    const currentVerifications = this._state() ?? [];
    const existingIndex = currentVerifications.findIndex(
      (v) => v.document_verification_id === update.document_verification_id,
    );

    if (existingIndex >= 0) {
      const existing = currentVerifications[existingIndex];
      // Only update if newer
      if (new Date(update.updated_at) >= new Date(existing.updated_at)) {
        const updatedVerifications = [...currentVerifications];
        updatedVerifications[existingIndex] = update;
        this._setStateIfInitialized(updatedVerifications);
      }
    } else {
      // Add new verification
      this._setStateIfInitialized([...currentVerifications, update]);
    }
  }

  /**
   * Updates multiple verifications at once.
   *
   * @param updates Array of verifications to update
   */
  updateVerifications(updates: DocumentVerificationUpdateRTM[]) {
    updates.forEach((update) => this.updateVerification(update));
  }

  /**
   * Manually syncs document verifications from the server.
   *
   * @returns Promise that resolves when the sync is complete
   */
  async syncDocumentVerifications() {
    this._loading.set(true);
    try {
      const verifications = await lastValueFrom(this._fetchDocumentVerifications$());
      this._setStateIfInitialized(verifications ?? []);
    } catch (err) {
      this._logger.error('Failed to sync document verifications', { error: err });
      throw err;
    } finally {
      this._loading.set(false);
    }
  }

  /**
   * Clear all cached verifications (e.g., on logout).
   */
  clear(): void {
    this._setStateIfInitialized(null);
    this._loading.set(true);
  }
}
